/**
 * The front page. The first screenful is all creature, with the buttons in a
 * row beneath it; the day's totals and what has been logged are further down.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api, ApiError, type Day, type Entry } from "../lib/api";
import { eat, momentOf, moodFor, poopAroundLine, reactToTap, sip, type Moment } from "../lib/creature";
import { pickUp, poopsOnTheGround, poopsSoFar } from "../lib/poops";
import { Creature, PoopPile } from "../components/Creature";
import { Totals } from "../components/Totals";
import { servingTotal } from "../../server/serving";
import { clockTime, describe, shift, today } from "../lib/day";

const icons: Record<string, string> = { food: "🍽️", drink: "🥤", exercise: "🏃" };

/**
 * The line drawings on the buttons. Each is a list of pen strokes on a 24 by 24
 * grid; the colour and line weight are set in styles.css under .home-button.
 */
const buttonDrawings = {
  today: [
    "M7 15h10v4a2 2 0 0 1 -2 2h-6a2 2 0 0 1 -2 -2v-4z",
    "M12 9a6 6 0 0 0 -6 -6h-3v2a6 6 0 0 0 6 6h3",
    "M12 11a6 6 0 0 1 6 -6h3v1a6 6 0 0 1 -6 6h-3",
    "M12 15v-6",
  ],
  add: ["M19 3v12h-5c-.023 -3.681 .184 -7.406 5 -12zm0 12v6h-1v-3m-10 -14v17m-3 -17v3a3 3 0 1 0 6 0v-3"],
  scan: [
    "M5 7h1a2 2 0 0 0 2 -2a1 1 0 0 1 1 -1h6a1 1 0 0 1 1 1a2 2 0 0 0 2 2h1a2 2 0 0 1 2 2v9a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-9a2 2 0 0 1 2 -2",
    "M9 13a3 3 0 1 0 6 0a3 3 0 0 0 -6 0",
  ],
  settings: [
    "M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065",
    "M9 12a3 3 0 1 0 6 0a3 3 0 0 0 -6 0",
  ],
};

function ButtonIcon({ name }: { name: keyof typeof buttonDrawings }) {
  return (
    <span className="home-button__icon">
      <svg viewBox="0 0 24 24" aria-hidden="true">
        {buttonDrawings[name].map((stroke) => (
          <path key={stroke} d={stroke} />
        ))}
      </svg>
    </span>
  );
}

/** The row of buttons under the creature. They scroll away with the page. */
function HomeButtons({ onToday }: { onToday: () => void }) {
  return (
    <nav className="home-buttons">
      <button className="home-button" onClick={onToday}>
        <ButtonIcon name="today" />
        <span>Today</span>
      </button>
      <Link to="/add" className="home-button">
        <ButtonIcon name="add" />
        <span>Add</span>
      </Link>
      <Link to="/scan" className="home-button">
        <ButtonIcon name="scan" />
        <span>Scan</span>
      </Link>
      <Link to="/settings" className="home-button">
        <ButtonIcon name="settings" />
        <span>Settings</span>
      </Link>
    </nav>
  );
}

export function Today() {
  const [date, setDate] = useState(today());
  const [day, setDay] = useState<Day | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [moment, setMoment] = useState<Moment | null>(null);
  const [now, setNow] = useState(() => new Date());
  const location = useLocation();
  const navigate = useNavigate();
  const details = useRef<HTMLDivElement>(null);

  const load = useCallback(async (which: string) => {
    setError(null);
    try {
      setDay(await api.day(which));
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : String(problem));
    }
  }, []);

  useEffect(() => {
    void load(date);
  }, [date, load]);

  // Just logged something? The logging form says so when it sends her here.
  // It waits for the day to load, so the creature is on screen to enjoy it, and
  // then clears the message so reloading the page doesn't feed it again.
  const logged: Entry | undefined = location.state?.logged;
  useEffect(() => {
    if (!logged || !day) return;
    if (logged.kind === "food") setMoment(eat(logged.name));
    if (logged.kind === "drink") setMoment(sip(logged.name));
    window.scrollTo(0, 0);
    navigate(location.pathname, { replace: true, state: null });
  }, [logged, day, location.pathname, navigate]);

  // Moments end by themselves.
  useEffect(() => {
    if (!moment) return;
    const timer = setTimeout(() => setMoment(null), moment.ms);
    return () => clearTimeout(timer);
  }, [moment]);

  // Keep an eye on the clock, so a poop can arrive while she is looking.
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(timer);
  }, []);

  // A poop arriving while she watches gets a little wiggle. Ones that came
  // while the app was closed are just there when she gets back.
  const isToday = date === today();
  const poopCount = isToday ? poopsSoFar(date, now).length : 0;
  const lastPoopCount = useRef(poopCount);
  useEffect(() => {
    if (poopCount > lastPoopCount.current) {
      setMoment((current) => current ?? momentOf("pooping"));
    }
    lastPoopCount.current = poopCount;
  }, [poopCount]);

  // Taps close together count as one burst, so it knows when it has had enough.
  const tapsInARow = useRef({ count: 0, lastAt: 0 });

  async function remove(id: number) {
    // Deleting is the one thing here that cannot be undone, so it asks first.
    if (!confirm("Remove this from the day?")) return;
    await api.deleteEntry(id);
    void load(date);
  }

  if (error) {
    return (
      <>
        <h1 className="screen-title">Today</h1>
        <div className="notice notice--bad">{error}</div>
        {/* Tapping Today here tries again; the rest may well still work. */}
        <HomeButtons onToday={() => void load(date)} />
      </>
    );
  }

  if (!day) {
    return (
      <div className="empty">
        <div className="spinner" style={{ margin: "0 auto 10px" }} />
        Loading…
      </div>
    );
  }

  const feeling = moodFor(day, now);
  const poops = isToday ? poopsOnTheGround(date, now) : [];
  const line = moment?.line ?? (poops.length > 0 ? poopAroundLine(date) : feeling.line);

  function tapCreature() {
    // Eating, sipping and so on are short and it should get to finish them.
    if (moment && ["eating", "sipping", "pooping", "thanks"].includes(moment.kind)) return;
    const taps = tapsInARow.current;
    taps.count = Date.now() - taps.lastAt < 2500 ? taps.count + 1 : 1;
    taps.lastAt = Date.now();
    const reaction = reactToTap(feeling.mood, taps.count, moment?.kind);
    if (reaction.kind === "dizzy") taps.count = 0;
    setMoment(reaction);
  }

  return (
    <>
      <section className="home-hero">
        <div className="creature-stage">
          <div className="creature-yard">
            <Creature mood={feeling.mood} name={day.settings.creature_name} moment={moment} onTap={tapCreature} />
            {poops.map((poop) => (
              <PoopPile
                key={poop.id}
                poop={poop}
                onPick={() => setMoment(momentOf("thanks"))}
                onPicked={() => {
                  pickUp(date, poop.id);
                  setNow(new Date());
                }}
              />
            ))}
          </div>
          <div className="creature-name">{day.settings.creature_name}</div>
          <p className="creature-says">{line}</p>
        </div>
        <HomeButtons onToday={() => details.current?.scrollIntoView({ behavior: "smooth" })} />
      </section>

      <div className="home-details" ref={details}>
        <div className="day-nav">
          <button onClick={() => setDate(shift(date, -1))} aria-label="The day before">
            ‹
          </button>
          <h1 className="screen-title" style={{ margin: 0 }}>
            {describe(date)}
          </h1>
          <button
            onClick={() => setDate(shift(date, 1))}
            disabled={isToday}
            aria-label="The day after"
          >
            ›
          </button>
        </div>

        <Totals day={day} />

        <div className="card">
          <div className="spread" style={{ marginBottom: 6 }}>
            <h2 style={{ fontSize: "1rem" }}>Logged</h2>
            <Link to="/add" className="small" style={{ color: "var(--accent)" }}>
              + Add
            </Link>
          </div>

          {day.entries.length === 0 ? (
            <div className="empty">
              Nothing logged yet.
              <div style={{ marginTop: 14 }}>
                <Link to="/add" className="button">
                  Log something
                </Link>
              </div>
            </div>
          ) : (
            day.entries.map((entry) => (
              <div className="entry" key={entry.id}>
                <div className="entry__icon">{icons[entry.kind] ?? "🍽️"}</div>
                <div className="entry__main">
                  <div className="entry__name">{entry.name}</div>
                  <div className="muted small">
                    {clockTime(entry.logged_at)}
                    {entry.serving_amount != null && entry.serving_unit
                      ? ` · ${servingTotal(entry.serving_amount, entry.serving_unit, entry.servings)}`
                      : ""}
                    {entry.kind === "exercise"
                      ? entry.minutes
                        ? ` · ${Math.round(entry.minutes)} min`
                        : ""
                      : entry.calories != null
                        ? ` · ${Math.round(entry.calories)} kcal`
                        : ""}
                    {entry.kind === "exercise" && entry.calories
                      ? ` · −${Math.round(entry.calories)} kcal`
                      : ""}
                  </div>
                </div>
                <button
                  className="entry__delete"
                  onClick={() => void remove(entry.id)}
                  aria-label={`Remove ${entry.name}`}
                >
                  ✕
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}
